import DatePicker from "common/DatePicker/DatePicker";
import Button from "common/Buttons/Button";
import styled, { css, useTheme } from "styled-components";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import React, { useRef, useState } from "react";
import { dateIsLifetime } from "@shared/utils/date";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { LanguageNameSpaces, SubGroupUserMembership } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
dayjs.extend(utc);

interface ExpirationDatePickerProps {
  membership: SubGroupUserMembership;
  expiration?: string;
  updateExpiration: (
    membership: SubGroupUserMembership,
    date?: string,
    successCallback?: () => void
  ) => void;
  loading?: boolean;
}

const ExpirationDatePicker = ({
  membership,
  expiration,
  updateExpiration,
  loading,
}: ExpirationDatePickerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { isNewLook } = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const isLifetime = expiration && dateIsLifetime(expiration);
  const hasDate = !isLifetime && !!expiration;
  // New look: every state reads as text with the calendar button after it,
  // so a date is never clipped by the borderless picker input.
  const customInput = isNewLook || isLifetime || !expiration;
  const currentMomentValue = hasDate
    ? dayjs(expiration).utc(true).local()
    : dayjs();

  const curValue = useRef<string>(undefined);

  const handleSaveExpiration = () => {
    setPickerOpen(false);
    updateExpiration(membership, curValue.current);
  };

  const handleSetPickerOpen = (open) => {
    if (!open) {
      curValue.current = undefined;
    }
    setPickerOpen(open);
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {isNewLook && (
        <NewLookValue
          type="button"
          $muted={!expiration}
          onClick={() => setPickerOpen(!pickerOpen)}
        >
          {isLifetime && t("users.expiration.lifetime")}
          {!expiration && t("users.expiration.notSet")}
          {hasDate && currentMomentValue.format("YYYY-MM-DD HH:mm")}
        </NewLookValue>
      )}
      {customInput && !isNewLook && (
        <p
          onClick={() => setPickerOpen(!pickerOpen)}
          style={{
            margin: "0 0 0 13px",
          }}
        >
          {isLifetime && t("users.expiration.lifetime")}
          {!expiration && t("users.expiration.notSet")}
        </p>
      )}
      <StyledDatePicker
        value={(curValue.current || currentMomentValue) as any}
        allowClear={false}
        variant="borderless"
        showToday={false}
        showNow={false}
        showTime={{ format: "HH:mm" }}
        format={"YYYY-MM-DD HH:mm"}
        open={pickerOpen}
        onOpenChange={handleSetPickerOpen}
        $hideInput={customInput}
        onPickerValueChange={(value) => (curValue.current = value as any)}
        renderExtraFooter={() => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50px",
            }}
            onClick={handleSaveExpiration}
          >
            <Button type="primary" loading={loading}>
              {t("users.confirm.save")}
            </Button>
          </div>
        )}
        panelRender={(panelNode) => (
          <DatePickerWrapper>{panelNode}</DatePickerWrapper>
        )}
      />
    </div>
  );
};

export default ExpirationDatePicker;

const StyledDatePicker = styled(DatePicker)<{ $hideInput?: boolean }>`
  ${(props) =>
    props.$hideInput &&
    `
    .ant-picker-input > input {
      display: none;
    }
  `}
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Just the calendar button after the value, on the row's own
         background (hover included). */
      &&.ant-picker {
        width: auto;
        padding: 4px 6px;
        margin-left: 4px;
        background: transparent !important;
        color: ${getNewLookTokens(p.theme).secondary};
      }
      .ant-picker-input {
        flex: none;
        width: auto;
      }
    `}
`;

const NewLookValue = styled.button<{ $muted?: boolean }>`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(p) =>
    p.$muted
      ? getNewLookTokens(p.theme).muted
      : getNewLookTokens(p.theme).text};
  font: inherit;
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).accent};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
const DatePickerWrapper = styled.div`
  .ant-picker-ranges {
    display: none !important;
  }
  .ant-picker-footer-extra {
    border-bottom: none;
  }
`;
